import React, { useEffect, useRef, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { addEnquiriesData } from "../services/enquirieService";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext"; // ✅ ThemeContext import

const EnquiryPopup = ({ isOpen, onClose }) => {
  const { theme } = useContext(ThemeContext); // ✅ Use theme
  const backdropRef = useRef(null);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    travelDate: "",
    totalPerson: "",
    destination: "",
    leavingFrom: "",
    bookingWithin: "",
    totalDays: "7",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (backdropRef.current && e.target === backdropRef.current) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name) newErrors.name = "Name is required";
      if (!formData.phone) newErrors.phone = "Phone is required";
      if (!formData.travelDate) newErrors.travelDate = "Travel date is required";
      if (!formData.totalPerson) newErrors.totalPerson = "Number of adults is required";
    } else {
      if (!formData.destination) newErrors.destination = "Destination is required";
      if (!formData.leavingFrom) newErrors.leavingFrom = "Departure city is required";
      if (!formData.bookingWithin) newErrors.bookingWithin = "Booking timeline is required";
    }
    return newErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const validationErrors = validateStep(1);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateStep(2);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        travelDate: formData.travelDate,
        destination: formData.destination,
        leavingFrom: formData.leavingFrom,
        totalPerson: parseInt(formData.totalPerson.match(/\d+/)[0]),
        totalDays: 7,
        bookingWithin:
          formData.bookingWithin === "1-2 Days" ? 2 :
          formData.bookingWithin === "1 Week" ? 7 :
          formData.bookingWithin === "2 Weeks" ? 14 : 30
      };

      await addEnquiriesData(payload);
      toast.success("Enquiry submitted successfully!");
      onClose();
      navigate("/thank-you");
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    }
  };

  const formFields = {
    step1: [
      { id: "name", placeholder: "Enter Your Name *", type: "text", error: errors.name },
      { id: "phone", placeholder: "Enter Your Phone *", type: "tel", error: errors.phone },
      { id: "travelDate", placeholder: "dd-mm-yyyy", type: "date", error: errors.travelDate },
      {
        id: "totalPerson",
        placeholder: "Select Passengers *",
        type: "select",
        options: ["1 Adult", "2 Adults", "3 Adults", "4 Adults", "5+ Adults", "Other"],
        error: errors.totalPerson
      },
    ],
    step2: [
      {
        id: "destination",
        placeholder: "Where do you want to go?",
        type: "select",
        options: ["Dubai", "Thailand", "Maldives", "Bali", "Singapore", "Sri Lanka", "Vietnam", "Kerala", "Goa", "Kashmir"],
        error: errors.destination
      },
      {
        id: "leavingFrom",
        placeholder: "Leaving From",
        type: "select",
        options: [
          "Ahmedabad", "Aurangabad", "Bangalore", "Bhopal", "Bhubaneshwar", "Chandigarh", "Chennai",
          "Coimbatore", "Delhi", "Hyderabad", "Jaipur", "Jammu", "Kochi", "Kolkata", "Lucknow",
          "Madurai", "Mangalore", "Mumbai", "Nagpur", "Patna", "Pune", "Raipur", "Vadodara",
          "Visakhapatnam", "Other"
        ],
        error: errors.leavingFrom
      },
      {
        id: "bookingWithin",
        placeholder: "Booking Timeline",
        type: "select",
        options: ["1-2 Days", "1 Week", "2 Weeks", "1 Month"],
        error: errors.bookingWithin
      },
    ],
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={backdropRef}
          className={`${theme === "dark" ? "dark" : ""} fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-lg p-6 sm:p-8"
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-white"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <h2 className="text-xl sm:text-2xl font-bold text-center text-teal-600 dark:text-teal-400 mb-6">
              Get Free Quote
            </h2>

            <form
              onSubmit={step === 1 ? handleNext : handleSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {(step === 1 ? formFields.step1 : formFields.step2).map((field) => (
                <div key={field.id} className="col-span-1">
                  {field.type === "select" ? (
                    <>
                      <select
                        id={field.id}
                        value={formData[field.id]}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-full border ${
                          field.error ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                        } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-teal-400 focus:outline-none`}
                      >
                        <option value="">{field.placeholder}</option>
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      {field.error && (
                        <p className="text-red-500 text-xs mt-1">{field.error}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type={field.type}
                        id={field.id}
                        value={formData[field.id]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        className={`w-full px-4 py-3 rounded-full border ${
                          field.error ? "border-red-500" : "border-gray-300 dark:border-gray-600"
                        } bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-teal-400 focus:outline-none`}
                      />
                      {field.error && (
                        <p className="text-red-500 text-xs mt-1">{field.error}</p>
                      )}
                    </>
                  )}
                </div>
              ))}

              {/* Buttons */}
              <div className="col-span-1 sm:col-span-2 mt-4 flex gap-3">
                {step === 2 && (
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white py-3 rounded-full text-lg font-semibold"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full text-lg font-semibold"
                >
                  {step === 1 ? "Next" : "Get Quote"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryPopup;
